/**
 * Registry Platform Card Component
 * Displays a registry platform option with logo and quick links
 */

import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { RegistryPlatformInfo } from '@/types/celebrationRegistry.types';

interface RegistryPlatformCardProps {
  platform: RegistryPlatformInfo;
  onCreateClick: (url: string) => void;
  onAddUrlClick: (platform: RegistryPlatformInfo) => void;
}

export const RegistryPlatformCard: React.FC<RegistryPlatformCardProps> = ({
  platform,
  onCreateClick,
  onAddUrlClick
}) => {
  return (
    <div className="flex flex-col items-center p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
      {/* Logo */}
      <img
        src={platform.logo_url}
        alt={platform.name}
        className="h-16 w-auto mb-4 object-contain"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />

      {/* Name */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
        {platform.name}
      </h3>

      {/* Features (if any) */}
      {platform.features.length > 0 && (
        <div className="mb-4 text-center">
          <p className="text-xs text-gray-500 mb-2">Popular features:</p>
          <div className="flex flex-wrap gap-1 justify-center mb-4">
            {platform.features.slice(0, 2).map((feature) => (
              <span
                key={feature}
                className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
              >
                {feature}
              </span>
            ))}
            {platform.features.length > 2 && (
              <span className="text-xs text-gray-500">
                +{platform.features.length - 2} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 w-full">
        {platform.registry_url && (
          <Button
            onClick={() => onCreateClick(platform.registry_url!)}
            variant="default"
            size="sm"
            className="flex-1"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Create
          </Button>
        )}
        <Button
          onClick={() => onAddUrlClick(platform)}
          variant="outline"
          size="sm"
          className={platform.registry_url ? 'flex-1' : 'w-full'}
        >
          Add URL
        </Button>
      </div>
    </div>
  );
};
